import { ApiError } from "./api.js";

/** What the app understands of an upload response, whatever shape it arrived in. */
export interface UploadResponseLike {
  status: number;
  /** Parsed JSON body; `{}` when the response was not JSON. */
  body: unknown;
  /** True when the body was not JSON — e.g. an HTML error page from the proxy. */
  nonJson?: boolean;
}

/**
 * A readable message for a failed upload.
 *
 * Our API answers with `{ error, code }` (the rate limiter with `{ message }`),
 * and that message is shown with its status. A reverse proxy or WAF in front
 * of the API answers with an HTML page instead, which carries nothing useful
 * for a person — so the status alone decides the wording there. The 403 case
 * is the one production hit: ModSecurity refusing a PDF before the API ever
 * saw it.
 */
export function uploadErrorMessage({ status, body, nonJson }: UploadResponseLike): string {
  const payload = (body ?? {}) as { error?: string; message?: string; code?: string };
  const server = payload.error ?? payload.message;

  if (server && !nonJson) {
    // The malware message already says everything; a status suffix is noise.
    return payload.code === "MALWARE_DETECTED" ? server : `${server} (${status})`;
  }

  if (status === 403) return "Upload blocked by the server's security filter (403).";
  if (status === 413) return "That file is too large for the server to accept (413).";
  if (status === 502 || status === 503 || status === 504) {
    return `The server is unavailable right now (${status}). Please try again shortly.`;
  }
  return `Upload failed (${status}).`;
}

/** The ApiError a caller should throw for a failed upload response. */
export function uploadFailure(response: UploadResponseLike): ApiError {
  const code = ((response.body ?? {}) as { code?: string }).code;
  return new ApiError(uploadErrorMessage(response), response.status, response.nonJson ? undefined : code);
}

/** Message for anything thrown out of an upload attempt. */
export function uploadThrownMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Could not upload that file — check your connection and try again.";
}
